import { Stack } from '@mui/material';
import { autobind } from 'core-decorators';
import PropTypes from 'prop-types';
import { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import Map from '/@/components/map';
import Spinner from '/@/components/spinner';
import { useFieldById } from '/@/hooks/fields';
import { deleteFieldFromAPI } from '/@/store/fields/apiProviders';
import { setSelectedFeature } from '/@/store/nav/actions';
import { getSelectedFeature } from '/@/store/nav/selectors';
import { getSelectedField } from '/@/store/private-data/selected-field/selectors';
import { getAppInstance, getSessionCountry, getUser } from '/@/store/session/selectors';
import { mpTrackEvent } from '/@/store/tracking/mixpanel-events';
import { setField } from '/@/store/weather/actions';
import { getWeatherFeatureEnabled } from '/@/store/weather/selectors';
import { isMobile } from '/@/utils';
import { reverse } from '/@/utils/router';
import ErrorView from '/@/views/error-view';
import FieldInfo from '../../../components/field-info-v2';
import WeatherComponent from './field-weather';
import styles from './styles.module.css';

@connect(
    (state, props) => {
        const appInstance = getAppInstance(state);
        return {
            field,
            offline: state.network.offline,
            selectedFeature: getSelectedFeature(state),
            weatherEnabled: getWeatherFeatureEnabled(state),
            appInstance,
            country: getSessionCountry(state),
            user: getUser(state),
            selectedField: getSelectedField(state),
        };
    },
    (dispatch) => bindActionCreators({ setSelectedFeature, setField }, dispatch)
)
@autobind
class FieldDetail extends Component {
    static propTypes = {
        fieldId: PropTypes.string.isRequired,
        view: PropTypes.string,
        isList: PropTypes.bool,
    };

    static contextTypes = {
        history: PropTypes.object,
        units: PropTypes.object,
    };

    state = {
        showNitrogenRecommendationWarning: false,
        showMap: true,
    };

    async componentDidMount() {
        this.fitMap(this.props.field);
        this.props.setField(this.props.fieldId);
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        this.fitMap(nextProps.field);
    }

    fitMap(field) {
        if (field && this.map) {
            // Max zoom is based on the availability of tiles and a reasonable zoom to view an average field.
            this.map.fitPoints(field.geometry, 16);
        }
    }

    handleShowMap() {
        this.setState((prevState) => ({ showMap: !prevState.showMap }));
    }

    setMapRef(ref) {
        this.map = ref;
    }

    renderMap() {
        return <Map ref={this.setMapRef} fields={[this.props.field]} offline={this.props.offline} noGeocoder />;
    }

    renderFieldInfo() {
        return (
            <Fragment>
                {!this.props.isList ? (
                    <div className={styles.container}>
                        <FieldInfo
                            field={this.props.field}
                            onDelete={this.handleDelete}
                            view={this.props.view}
                            showIconMap
                            showMap={!!this.state.showMap}
                            onClickShowMap={() => this.handleShowMap()}
                            showArrow={this.props.view !== 'weather'}
                            hideOverlays
                        >
                            {isMobile() && <WeatherComponent fieldId={this.props.field} view='fieldDetail' />}
                        </FieldInfo>
                    </div>
                ) : (
                    <FieldList />
                )}
            </Fragment>
        );
    }

    handleNewNitrogenRecommendation() {
        const { field } = this.props;

        if (field.geometry.length == 1) {
            this.setState({ showNitrogenRecommendationWarning: true });
            return;
        }

        mpTrackEvent('N-Smart Recommendation began', {
            entry_source: 'field-list',
        });
        this.context.history(
            reverse('nitrogenRecommendation.NitrogenRecommendationCreate', {
                params: { fieldId: field.id },
            })
        );
    }

    showNitrogenRecommendationWarningClose() {
        this.setState({ showNitrogenRecommendationWarning: false });
    }

    redirectToNewField() {
        this.context.history(reverse('fields.NewField'));
    }

    render() {
        if (!this.props.field) return <ErrorView />;
        return (
            <div className={styles.root}>
                <div className={styles.sidebar}>{this.renderFieldInfo()}</div>
                <div className={this.state.showMap ? styles.content : styles.noContent}>
                    {this.props.weatherFeatureEnabled && !isMobile() && (
                        <WeatherComponent fieldId={this.props.fieldId} />
                    )}
                    {this.renderMap()}
                </div>
            </div>
        );
    }

    handleDelete() {
        this.context.history(reverse('Home'));
        deleteFieldFromAPI({ fieldId: this.props.field.id, country: this.props.country.code });
    }
}
const FieldDetailWrapper = (props: any) => {
    const fieldQuery = useFieldById({ id: props.fieldId });
    if (fieldQuery.isValidating)
        return (
            <Stack justifyContent='center' alignItems='center'>
                <Spinner />
            </Stack>
        );
    return <FieldDetail {...props} field={fieldQuery.data} />;
};
export default FieldDetailWrapper;
